import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStudentData, type StudentAssignment, type StudentSubmission } from '@/hooks/useStudentData';
import { submissionStatusMap, getStatusMeta } from '@/lib/statusMap';
import { formatFileSize, openSubmissionFile, downloadSubmissionFile } from '@/lib/fileDownload';
import StatusBadge from '@/components/base/StatusBadge';
import SubmitModal from './components/SubmitModal';

function formatDeadline(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

function isOverdue(deadline: string | null): boolean {
  if (!deadline) return false;
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

export default function StudentOdevler() {
  const { user } = useAuth();
  const { group, assignments, submissions, loading, error, refetch } = useStudentData();

  const [submitTarget, setSubmitTarget] = useState<StudentAssignment | null>(null);
  const [busySub, setBusySub] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const submissionFor = useMemo(
    () => (assignmentId: string) => submissions.find((s) => s.assignment_id === assignmentId) ?? null,
    [submissions],
  );

  function openSubmit(a: StudentAssignment) {
    setFileError(null);
    setSubmitTarget(a);
  }

  async function handleOpenSubmission(sub: StudentSubmission) {
    if (!sub.file_path) return;
    setBusySub(sub.id);
    setFileError(null);
    try {
      await openSubmissionFile(sub.file_path);
    } catch (err) {
      setFileError((err as Error).message || 'Dosya açılamadı.');
    } finally {
      setBusySub(null);
    }
  }

  async function handleDownloadSubmission(sub: StudentSubmission) {
    if (!sub.file_path) return;
    setBusySub(sub.id);
    setFileError(null);
    try {
      await downloadSubmissionFile(sub.file_path, sub.file_name ?? undefined);
    } catch (err) {
      setFileError((err as Error).message || 'Dosya indirilemedi.');
    } finally {
      setBusySub(null);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ödevler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Ödevler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={refetch} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ödevlerim</h1>
        <p className="mt-1 text-sm text-foreground-600">Öğretmeninizin size verdiği ödevleri görüntüleyin ve teslim edin.</p>
      </div>

      {fileError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <i className="ri-error-warning-line mt-0.5 text-base" />
          <span>{fileError}</span>
        </div>
      )}

      {!group ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-task-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir grup bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Ders içerikleriniz tanımlandığında burada görünecek.</p>
        </div>
      ) : assignments.length === 0 ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-task-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir ödev bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Öğretmeniniz ödev gönderdiğinde burada görünecek.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((a) => {
            const sub = submissionFor(a.id);
            const subMeta = sub ? getStatusMeta(sub.status, submissionStatusMap) : null;
            const overdue = isOverdue(a.deadline) && !sub;

            return (
              <div
                key={a.id}
                className={`rounded-xl border bg-white p-5 ${
                  overdue ? 'border-red-200' : 'border-background-200'
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground-950 text-base">{a.title}</h3>
                      {a.required && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
                          <i className="ri-star-fill text-[10px]" /> Zorunlu
                        </span>
                      )}
                      {overdue && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                          <i className="ri-alarm-warning-line text-[10px]" /> Süresi geçti
                        </span>
                      )}
                    </div>

                    {a.description && (
                      <p className="text-sm text-foreground-600 whitespace-pre-wrap">{a.description}</p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-calendar-line" />
                        Son teslim: <strong className="font-semibold text-foreground-700">{formatDeadline(a.deadline)}</strong>
                      </span>
                      {a.link && (
                        <a
                          href={a.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 font-semibold text-primary-600 transition hover:text-primary-700 whitespace-nowrap"
                        >
                          <i className="ri-external-link-line" /> Ödev Linki
                        </a>
                      )}
                    </div>

                    {/* Öğretmenin eklediği dosya — file_url varlığı asıl ölçüt */}
                    {a.file_url ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <a
                          href={a.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700 transition hover:bg-primary-100 cursor-pointer"
                        >
                          <i className="ri-eye-line" /> Ödev Dosyasını Aç
                        </a>
                        <a
                          href={a.file_url}
                          download
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-download-2-line" /> PDF İndir
                        </a>
                      </div>
                    ) : (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground-400">
                        <i className="ri-file-info-line" /> Bu ödev için henüz dosya eklenmemiş.
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                    {subMeta ? (
                      <StatusBadge meta={subMeta} />
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-700">
                        <i className="ri-hourglass-line" /> Teslim Edilmedi
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => openSubmit(a)}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                        sub
                          ? 'border border-background-300 bg-white text-foreground-700 hover:bg-background-100'
                          : 'bg-primary-500 text-white hover:bg-primary-600'
                      }`}
                    >
                      <i className={sub ? 'ri-edit-line' : 'ri-send-plane-line'} />
                      {sub ? 'Teslimi Güncelle' : 'Ödevi Teslim Et'}
                    </button>
                  </div>
                </div>

                {sub?.note && (
                  <div className="mt-3 rounded-lg bg-background-50 px-4 py-3">
                    <p className="text-xs font-semibold text-foreground-500">Teslim notunuz:</p>
                    <p className="mt-0.5 text-sm text-foreground-700 whitespace-pre-wrap">{sub.note}</p>
                  </div>
                )}

                {sub?.link && (
                  <div className="mt-3">
                    <a
                      href={sub.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                    >
                      <i className="ri-link" /> Ek Kaynak Linki
                    </a>
                  </div>
                )}

                {sub?.file_name && (
                  <div className="mt-3 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-foreground-500">
                        Teslim tarihi: <span className="font-normal text-foreground-600">{formatDateTime(sub.submitted_at)}</span>
                      </p>
                      <p className="mt-1 flex items-center gap-1.5 truncate text-sm font-medium text-foreground-800">
                        <i className="ri-file-line text-foreground-500" />
                        <span className="truncate">{sub.file_name}</span>
                        {sub.file_size ? (
                          <span className="text-xs text-foreground-400">({formatFileSize(sub.file_size)})</span>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenSubmission(sub)}
                        disabled={busySub === sub.id}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 transition hover:bg-primary-100 disabled:opacity-70 cursor-pointer"
                      >
                        {busySub === sub.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-eye-line" />}
                        Dosyayı Görüntüle
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadSubmission(sub)}
                        disabled={busySub === sub.id}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 disabled:opacity-70 cursor-pointer"
                      >
                        <i className="ri-download-2-line" />
                        İndir
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SubmitModal
        assignment={submitTarget ? { id: submitTarget.id, title: submitTarget.title } : null}
        existing={submitTarget ? submissionFor(submitTarget.id) : null}
        userId={user?.id ?? ''}
        onClose={() => setSubmitTarget(null)}
        onSubmitted={refetch}
      />
    </div>
  );
}